import { useRef, useState } from 'react';
import { scrollToAnchor } from '../utilities/scrollUtil.js';

export default function useFileUpload({ maxResumes = 8, onError }) {
  const [bulkFiles, setBulkFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const [editingFilename, setEditingFilename] = useState(null);
  const [tempFilename, setTempFilename] = useState('');
  const [previewFile, setPreviewFile] = useState(null);
  const [previewFileIndex, setPreviewFileIndex] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [editedFiles, setEditedFiles] = useState({});
  const [pendingFilenameInputs, setPendingFilenameInputs] = useState({});
  const [submittedFilenameInputs, setSubmittedFilenameInputs] = useState({});
  const [isFirstUpload, setFirstUpload] = useState(true);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleBulkChange({ target: { files } });
    }
  };
  const handleClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleRemoveFile = (index) => {
    const updatedFiles = [...bulkFiles];
    updatedFiles.splice(index, 1);
    setBulkFiles(updatedFiles);

    if (editingFilename === index) {
      setEditingFilename(null);
      setTempFilename('');
    } else if (typeof editingFilename === 'number' && editingFilename > index) {
      setEditingFilename((cur) => cur - 1);
    }
    if (updatedFiles.length === 0) {
      setEditedFiles({});
      setPendingFilenameInputs({});
      setSubmittedFilenameInputs({});
      return;
    }
    setPendingFilenameInputs((prev) => {
      const shifted = {};
      Object.entries(prev).forEach(([key, baseName]) => {
        const idx = Number(key);
        if (idx < index) shifted[idx] = baseName;
        else if (idx > index) shifted[idx - 1] = baseName;
      });
      return shifted;
    });
    setEditedFiles((prev) => {
      const shifted = {};
      Object.entries(prev).forEach(([key, wasEdited]) => {
        const idx = Number(key);
        if (!wasEdited) return;
        if (idx < index) shifted[idx] = true;
        else if (idx > index) shifted[idx - 1] = true;
      });
      return shifted;
    });
  };

  const handleFilenameEdit = (index, newName) => {
    if (!bulkFiles[index]) return null;
    const originalFile = bulkFiles[index];
    const originalExt = originalFile.name.split('.').pop().toLowerCase();
    const userInput = newName.trim();
    const newFilename = `${userInput}.${originalExt}`;
    setEditedFiles((prev) => ({ ...prev, [index]: true }));
    const renamedFile = new File([originalFile], newFilename, {
      type: originalFile.type,
      lastModified: originalFile.lastModified,
    });
    const updatedFiles = [...bulkFiles];
    updatedFiles[index] = renamedFile;
    setBulkFiles(updatedFiles);
    setEditingFilename(null);
    return userInput;
  };
  const handleEdit = (index, newName) => {
    const userInput = handleFilenameEdit(index, newName);
    if (userInput) {
      setPendingFilenameInputs((prev) => ({ ...prev, [index]: userInput }));
    }
  };

  const handleBulkChange = (e) => {
    const files = Array.from(e.target.files);
    const MAX_FILE_SIZE_MB = 1;
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
    const ALLOWED_FILE_TYPES = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
    ];

    const validFiles = [];
    const invalidFiles = [];
    files.forEach((file) => {
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        invalidFiles.push(`${file.name}: Invalid file type`);
      } else if (file.size > MAX_FILE_SIZE_BYTES) {
        invalidFiles.push(`${file.name}: Exceeds ${MAX_FILE_SIZE_MB}MB limit`);
      } else {
        validFiles.push(file);
      }
    });
    if (invalidFiles.length > 0) {
      onError?.(`Some files were invalid:\n${invalidFiles.join('\n')}\n\n.Only ${validFiles.length} valid files will be uploaded.`);
    } else {
      onError?.('');
    }

    const total = bulkFiles.length + validFiles.length;
    if (total > maxResumes) {
      onError?.(`You can only upload ${maxResumes} resumes.`);
      return;
    }
    const withOriginalIndexes = validFiles.map((file, idx) => Object.assign(file, { originalIndex: bulkFiles.length + idx }));
    setFirstUpload(false);
    const isFresh = bulkFiles.length === 0;
    setBulkFiles((prev) => [...prev, ...withOriginalIndexes]);
    if (isFresh) {
      setPendingFilenameInputs({});
      setEditedFiles({});
      setEditingFilename(null);
      setTempFilename('');
      setSubmittedFilenameInputs({});
    }
    scrollToAnchor('uploaded-resume-summary', -980);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return {
    // state
    bulkFiles,
    isDragging,
    fileInputRef,
    editingFilename,
    tempFilename,
    previewFile,
    previewFileIndex,
    numPages,
    pageNumber,
    editedFiles,
    pendingFilenameInputs,
    submittedFilenameInputs,
    isFirstUpload,
    // setters
    setPreviewFile,
    setPreviewFileIndex,
    setNumPages,
    setPageNumber,
    setEditingFilename,
    setTempFilename,
    setSubmittedFilenameInputs,
    setEditedFiles,
    setBulkFiles,
    setFirstUpload,
    // handlers
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleClick,
    handleRemoveFile,
    handleEdit,
    handleBulkChange,
  };
}


